import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { classifySendError } from "@/services/whatsapp-service";
import {
  sendTemplateSchema,
  WhatsAppSendRefusal,
  WhatsAppTemplate,
} from "@/types/whatsapp";
import { useApprovedTemplates, useSendTemplate } from "@/hooks/whatsapp.hooks";
import type { Reservation } from "@/types/reservation";
import type { Hotel } from "@/types/hotel";
import { useSendExternalMessage } from "@/components/pages/chats/hooks/use-send-external-message.hooks";

interface ReengagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hotelId: string;
  /**
   * Stream channel id for the unified FrontDesk thread. Template sends go
   * through `/chat/messages/external` so the template appears in Stream and is
   * audited in MessageDelivery before the WhatsApp provider call.
   */
  streamChannelId?: string | null;
  /** Direct WhatsApp inbox conversation id. Used only when Stream channel id is absent. */
  conversationId?: string | null;
  /** Optional navigation to Templates settings shown in the empty state. */
  onGoToTemplates?: () => void;
  /** Optional booking/hotel context used to prefill Guesteria template variables. */
  templateContext?: WhatsAppTemplatePrefillContext;
}

export interface WhatsAppTemplatePrefillContext {
  booking?: Reservation | null;
  hotel?: Pick<Hotel, "id" | "name"> | null;
}

/**
 * Maps a 409 refusal/re-engagement token to the dialog's inline error copy.
 * Full literal keys (not built at runtime) so i18n static extraction sees them.
 */
const TEMPLATE_REFUSAL_ERROR_KEY: Record<WhatsAppSendRefusal, string> = {
  WHATSAPP_NO_CONVERSATION:
    "communicationChannels.whatsappFlow.dialog.error.generic",
  TEMPLATE_NOT_APPROVED:
    "communicationChannels.whatsappFlow.dialog.error.templateNotApproved",
  WHATSAPP_TEMPLATE_NOT_FOUND:
    "communicationChannels.whatsappFlow.dialog.error.templateNotFound",
  REENGAGEMENT_DISABLED:
    "communicationChannels.whatsappFlow.dialog.error.reengagementDisabled",
  WHATSAPP_DISABLED:
    "communicationChannels.whatsappFlow.dialog.error.whatsappDisabled",
  KILL_SWITCH: "communicationChannels.whatsappFlow.dialog.error.killSwitch",
  WHATSAPP_RE_ENGAGEMENT_REQUIRED:
    "communicationChannels.whatsappFlow.dialog.error.reengagementRequired",
  WHATSAPP_RECIPIENT_NOT_OPTED_IN:
    "communicationChannels.whatsappFlow.dialog.error.recipientNotOptedIn",
  WHATSAPP_UNSUPPORTED_MEDIA_TYPE:
    "communicationChannels.whatsappFlow.dialog.error.generic",
  // Not expected on a template send, but map defensively to a generic message.
  WINDOW_CLOSED: "communicationChannels.whatsappFlow.dialog.error.generic",
};

export const ReengagementDialog: React.FC<ReengagementDialogProps> = ({
  open,
  onOpenChange,
  hotelId,
  streamChannelId,
  conversationId,
  onGoToTemplates,
  templateContext,
}) => {
  const { t } = useTranslation();
  const {
    data: templates,
    isLoading,
    isError,
  } = useApprovedTemplates(hotelId, open);

  const [selectedId, setSelectedId] = useState<string>("");
  const selectedTemplate =
    templates?.find((tpl) => tpl.id === selectedId) ?? null;

  const handleOpenChange = (next: boolean) => {
    if (!next) setSelectedId("");
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t("communicationChannels.whatsappFlow.dialog.title")}
          </DialogTitle>
          <DialogDescription>
            {t("communicationChannels.whatsappFlow.dialog.description")}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <p className="text-muted-foreground text-sm">
            {t("communicationChannels.whatsappFlow.dialog.loading")}
          </p>
        )}

        {isError && (
          <p className="text-destructive text-sm">
            {t("communicationChannels.whatsappFlow.dialog.loadError")}
          </p>
        )}

        {!isLoading && !isError && templates?.length === 0 && (
          <div className="flex flex-col items-start gap-2 rounded-md border border-dashed p-4">
            <p className="text-muted-foreground text-sm">
              {t("communicationChannels.whatsappFlow.dialog.empty")}
            </p>
            {onGoToTemplates && (
              <Button
                type="button"
                variant="new_link"
                className="h-auto p-0"
                onClick={onGoToTemplates}
              >
                {t("communicationChannels.whatsappFlow.dialog.emptyLink")}
              </Button>
            )}
          </div>
        )}

        {!isLoading && !isError && !!templates?.length && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wa-template">
                {t("communicationChannels.whatsappFlow.dialog.templateLabel")}
              </Label>
              <Select value={selectedId} onValueChange={setSelectedId}>
                <SelectTrigger id="wa-template">
                  <SelectValue
                    placeholder={t(
                      "communicationChannels.whatsappFlow.dialog.templatePlaceholder",
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((tpl) => (
                    <SelectItem key={tpl.id} value={tpl.id}>
                      <span className="flex items-center gap-2">
                        {tpl.name}
                        {tpl.hasQuickReplyButton && (
                          <Badge variant="new_secondary">
                            {t(
                              "communicationChannels.whatsappFlow.dialog.quickReplyBadge",
                            )}
                          </Badge>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedTemplate && (
              <TemplateVariableForm
                key={selectedTemplate.id}
                hotelId={hotelId}
                streamChannelId={streamChannelId}
                conversationId={conversationId}
                template={selectedTemplate}
                templateContext={templateContext}
                onClose={() => handleOpenChange(false)}
              />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

interface TemplateVariableFormProps {
  hotelId: string;
  streamChannelId?: string | null;
  conversationId?: string | null;
  template: WhatsAppTemplate;
  templateContext?: WhatsAppTemplatePrefillContext;
  onClose: () => void;
}

const TemplateVariableForm: React.FC<TemplateVariableFormProps> = ({
  hotelId,
  streamChannelId,
  conversationId,
  template,
  templateContext,
  onClose,
}) => {
  const { t } = useTranslation();
  const { mutateAsync: sendExternal, isPending: isSendingExternal } =
    useSendExternalMessage();
  const { mutateAsync: sendTemplate, isPending: isSendingTemplate } =
    useSendTemplate(hotelId, conversationId ?? "");
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const canSend = !!streamChannelId || !!conversationId;
  const isPending = isSendingExternal || isSendingTemplate;

  const formSchema = z.object({
    values: z
      .array(z.string().min(1, t("communicationChannels.validation.required")))
      .length(template.variables.length),
  });
  type FormData = z.infer<typeof formSchema>;
  const defaultVariableValues = useMemo(
    () => buildTemplateVariableDefaults(template, templateContext),
    [template, templateContext],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { values: defaultVariableValues },
  });

  useEffect(() => {
    reset({ values: defaultVariableValues });
  }, [defaultVariableValues, reset]);

  const onSubmit = async (data: FormData) => {
    // The send button is disabled until one legal delivery target is known, but
    // guard against a stale submit anyway.
    if (!canSend) return;
    setErrorKey(null);
    const variables = Object.fromEntries(
      template.variables.map((name, i) => [name, data.values[i]]),
    );

    try {
      if (streamChannelId) {
        await sendExternal({
          streamChannelId,
          text: buildTemplatePreviewText(template, t),
          channel: "WHATSAPP",
          whatsappTemplateName: `${template.name}:${template.language}`,
          whatsappTemplateVariables: variables,
        });
      } else {
        await sendTemplate(
          sendTemplateSchema.parse({
            templateId: template.id,
            variables,
          }),
        );
      }
      toast.success(t("communicationChannels.whatsappFlow.dialog.success"));
      onClose();
    } catch (error) {
      const classified = classifySendError(error);
      if (classified.kind === "reengage" || classified.kind === "refusal") {
        // The guest still can't be reached even by template (not approved /
        // can't re-engage / not opted in) — keep the dialog open so the agent
        // can pick a different template.
        setErrorKey(
          TEMPLATE_REFUSAL_ERROR_KEY[classified.token as WhatsAppSendRefusal] ??
            "communicationChannels.whatsappFlow.dialog.error.generic",
        );
      } else if (classified.kind === "upstream") {
        // 502 — the integration is down; not the agent's fault and a different
        // template won't help. Surface it but keep the dialog open.
        setErrorKey(
          classified.token === "WHATSAPP_AUTH_FAILED"
            ? "communicationChannels.whatsappFlow.dialog.error.authFailed"
            : "communicationChannels.whatsappFlow.dialog.error.unavailable",
        );
      } else {
        toast.error(
          t("communicationChannels.whatsappFlow.dialog.error.generic"),
        );
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      {template.variables.length > 0 && (
        <div className="flex flex-col gap-3">
          <p className="text-sm font-medium">
            {t("communicationChannels.whatsappFlow.dialog.variablesLabel")}
          </p>
          {template.variables.map((name, i) => (
            <div key={name} className="flex flex-col gap-1.5">
              <Label htmlFor={`wa-var-${name}`}>{name}</Label>
              <Input
                id={`wa-var-${name}`}
                autoComplete="off"
                {...register(`values.${i}` as const)}
              />
              {errors.values?.[i] && (
                <p className="text-destructive text-xs">
                  {errors.values[i]?.message}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {errorKey && <p className="text-destructive text-sm">{t(errorKey)}</p>}

      {!errorKey && !canSend && (
        <p className="text-destructive text-sm">
          {t("communicationChannels.whatsappFlow.dialog.resolveError")}
        </p>
      )}

      <DialogFooter>
        <Button
          type="button"
          variant="new_outline"
          onClick={onClose}
          disabled={isPending}
        >
          {t("communicationChannels.whatsappFlow.dialog.cancel")}
        </Button>
        <Button
          type="submit"
          variant="new_default"
          disabled={isPending || !canSend}
        >
          {isPending
            ? t("communicationChannels.whatsappFlow.dialog.sending")
            : t("communicationChannels.whatsappFlow.dialog.send")}
        </Button>
      </DialogFooter>
    </form>
  );
};

function buildTemplatePreviewText(
  template: WhatsAppTemplate,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  return t("communicationChannels.whatsappFlow.dialog.streamPreview", {
    template: template.name,
  });
}

function buildTemplateVariableDefaults(
  template: WhatsAppTemplate,
  context?: WhatsAppTemplatePrefillContext,
): string[] {
  const variableSources =
    template.metadata?.recommendedBinding?.variableSources ?? {};
  return template.variables.map((name) => {
    const source = variableSources[name] ?? inferVariableSource(name);
    return source ? (resolveVariableSource(source, context) ?? "") : "";
  });
}

function inferVariableSource(variableName: string): string | null {
  const normalized = variableName.toLowerCase();
  if (
    ["guest_name", "guest_first_name", "first_name", "var_1"].includes(
      normalized,
    )
  ) {
    return "guest.profile.firstName";
  }
  if (["guest_last_name", "last_name"].includes(normalized)) {
    return "guest.profile.lastName";
  }
  if (["hotel_name", "var_2"].includes(normalized)) {
    return "hotel.name";
  }
  if (
    ["arrival_date", "checkin_date", "check_in_date", "var_3"].includes(
      normalized,
    )
  ) {
    return "booking.arrivalDate|date:dd MMM yyyy";
  }
  if (
    ["departure_date", "checkout_date", "check_out_date"].includes(normalized)
  ) {
    return "booking.departureDate|date:dd MMM yyyy";
  }
  if (["booking_number", "reservation_number"].includes(normalized)) {
    return "booking.bookingNumber";
  }
  return null;
}

function resolveVariableSource(
  source: string,
  context?: WhatsAppTemplatePrefillContext,
): string | null {
  const [pathSpec, modifier] = source.split("|", 2);
  const raw = walkTemplatePath(pathSpec.trim(), context);
  if (raw == null) return null;
  if (!modifier) return stringifyTemplateValue(raw);

  const [modifierName, modifierArg] = modifier.split(":", 2);
  if (modifierName.trim() === "date") {
    return formatTemplateDate(raw, modifierArg?.trim() || "dd MMM yyyy");
  }
  return stringifyTemplateValue(raw);
}

function walkTemplatePath(
  path: string,
  context?: WhatsAppTemplatePrefillContext,
): unknown {
  const root = {
    booking: context?.booking,
    guest: context?.booking?.guest,
    hotel: context?.hotel,
  };
  return path
    .split(".")
    .filter(Boolean)
    .reduce<unknown>((current, segment) => {
      if (current == null || typeof current !== "object") return undefined;
      return (current as Record<string, unknown>)[segment];
    }, root);
}

function stringifyTemplateValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (value instanceof Date) return value.toISOString();
  return "";
}

function formatTemplateDate(value: unknown, pattern: string): string | null {
  let date: Date | null = null;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === "string" || typeof value === "number") {
    date = new Date(value);
  }
  if (!date || Number.isNaN(date.getTime())) return null;
  try {
    return format(date, pattern);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}
